import { useNavigate } from 'react-router';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import type { PracticeSeedItem } from '@/types/seeds';

interface SeedsTableProps {
  seeds: PracticeSeedItem[];
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleRow?: (id: string) => void;
  onToggleAllOnPage?: (checked: boolean) => void;
}

const difficultyColor = (level: number) => {
  if (level <= 2) return 'default';
  if (level <= 3) return 'secondary';
  return 'destructive';
};

export function SeedsTable({
  seeds,
  selectable = false,
  selectedIds,
  onToggleRow,
  onToggleAllOnPage,
}: SeedsTableProps) {
  const navigate = useNavigate();

  const selected = selectedIds ?? new Set<string>();
  const allSelected = seeds.length > 0 && seeds.every((s) => selected.has(s.id));
  const someSelected = seeds.some((s) => selected.has(s.id));

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onCheckedChange={(checked) => onToggleAllOnPage?.(!!checked)}
                  aria-label="Select all on page"
                />
              </TableHead>
            )}
            <TableHead className="w-12">#</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Difficulty</TableHead>
            <TableHead>Assigned</TableHead>
            <TableHead>Completion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {seeds.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={selectable ? 8 : 7}
                className="text-center text-muted-foreground"
              >
                No seeds found.
              </TableCell>
            </TableRow>
          ) : (
            seeds.map((s) => {
              const isSelected = selected.has(s.id);
              return (
                <TableRow
                  key={s.id}
                  className="cursor-pointer"
                  data-state={isSelected ? 'selected' : undefined}
                  onClick={() => navigate(`/seeds/${s.id}`)}
                >
                  {selectable && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleRow?.(s.id)}
                        aria-label={`Select ${s.title}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="text-muted-foreground">{s.seed_number}</TableCell>
                  <TableCell className="font-medium">{s.title}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{s.problem_category}</Badge>
                  </TableCell>
                  <TableCell>{s.estimated_minutes} min</TableCell>
                  <TableCell>
                    <Badge variant={difficultyColor(s.difficulty_level)}>
                      {s.difficulty_level}/5
                    </Badge>
                  </TableCell>
                  <TableCell>{s.times_assigned}</TableCell>
                  <TableCell>{s.completion_rate}%</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
